import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { MonthData, ForecastSettings, ActualMonthData, YearlyData } from "./types";
import { MONTHS } from "./constants";

interface ForecastTableProps {
  forecastData: MonthData[];
  actualData: Record<number, ActualMonthData>;
  actualDataByYear: YearlyData<ActualMonthData>;
  teenForecast: Record<number, number>;
  mentorsLeaving: Record<number, number>;
  mentorsLeavingByYear: YearlyData<number>;
  settings: ForecastSettings;
  selectedYear: number;
  currentMonth: number;
  expandedRows: Record<string, boolean>;
  prevYearDecemberStaff: { mentors: number; leads: number; supervisors: number };
  onTeenForecastChange: (monthIndex: number, value: string) => void;
  onMentorsLeavingChange: (monthIndex: number, value: string) => void;
  onToggleRow: (rowKey: string) => void;
}

export function ForecastTable({
  forecastData,
  actualData,
  actualDataByYear,
  mentorsLeaving,
  mentorsLeavingByYear,
  settings,
  selectedYear,
  currentMonth,
  expandedRows,
  prevYearDecemberStaff,
  onTeenForecastChange,
  onMentorsLeavingChange,
  onToggleRow,
}: ForecastTableProps) {
  const getNewMentorsNeeded = (data: MonthData, index: number) => {
    const prevData = index > 0 ? forecastData[index - 1] : null;
    const prevMentors = prevData
      ? (prevData.actualMentors ?? prevData.idealMentors)
      : prevYearDecemberStaff.mentors;
    const prevMonthIndex = index > 0 ? index - 1 : 11;
    const prevYearForLeaving = index > 0 ? selectedYear : selectedYear - 1;
    const prevActualLeaving = actualDataByYear[prevYearForLeaving]?.[prevMonthIndex]?.deactivatedMentors;
    const prevLeaving = prevActualLeaving ?? (mentorsLeavingByYear[prevYearForLeaving]?.[prevMonthIndex] ?? 0);
    return data.idealMentors - prevMentors + prevLeaving;
  };

  const getNewLeadsNeeded = (data: MonthData, index: number) => {
    const prevData = index > 0 ? forecastData[index - 1] : null;
    const prevLeads = prevData
      ? (prevData.actualLeads ?? prevData.idealLeads)
      : prevYearDecemberStaff.leads;
    return data.idealLeads - prevLeads;
  };

  const getNewSupervisorsNeeded = (data: MonthData, index: number) => {
    const prevData = index > 0 ? forecastData[index - 1] : null;
    const prevSupervisors = prevData
      ? (prevData.actualSupervisors ?? prevData.idealSupervisors)
      : prevYearDecemberStaff.supervisors;
    return data.idealSupervisors - prevSupervisors;
  };

  const formatDelta = (value: number) => (value > 0 ? `+${value}` : value);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Recruiting Forecast (Ideal)</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-b-2 bg-gray-100">
                <TableHead className="w-[180px] font-semibold">Metric</TableHead>
                {MONTHS.map((month, index) => (
                  <TableHead
                    key={month}
                    className={`text-center font-semibold ${index <= currentMonth ? "bg-blue-100" : ""}`}
                  >
                    {month}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Teens Row */}
              <TableRow>
                <TableCell className="font-medium">
                  <div className="flex flex-col">
                    <span>Teens</span>
                    <span className="text-xs text-gray-400">(actual / forecast)</span>
                  </div>
                </TableCell>
                {forecastData.map((data) => (
                  <TableCell key={data.month} className={`text-center ${data.isPast ? "bg-blue-50" : ""}`}>
                    {data.isPast ? (
                      <span className="font-semibold text-blue-600">{data.forecastedTeens.toLocaleString()}</span>
                    ) : (
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        defaultValue={data.forecastedTeens}
                        key={`${selectedYear}-${data.monthIndex}-${data.forecastedTeens}`}
                        onBlur={(e) => onTeenForecastChange(data.monthIndex, e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                        className="w-16 appearance-none border-b-2 border-dashed border-green-400 bg-transparent py-0.5 text-center font-semibold text-green-600 outline-none transition-all focus:border-green-600 focus:border-solid"
                      />
                    )}
                  </TableCell>
                ))}
              </TableRow>

              {/* Total Mentors Row */}
              <TableRow>
                <TableCell className="font-medium">Total Mentors</TableCell>
                {forecastData.map((data) => (
                  <TableCell key={data.month} className={`text-center font-semibold ${data.isPast ? "bg-blue-50" : ""}`}>
                    {data.isPast && data.actualMentors !== undefined ? data.actualMentors : data.idealMentors}
                  </TableCell>
                ))}
              </TableRow>

              {/* New Mentors Needed Row */}
              <TableRow
                className="cursor-pointer bg-green-50 hover:bg-green-100"
                onClick={() => onToggleRow("mentors")}
              >
                <TableCell className="font-medium text-green-700">
                  <span className="flex items-center gap-1">
                    <span className={`transition-transform ${expandedRows.mentors ? "rotate-90" : ""}`}>▶</span>
                    New Mentors Needed
                  </span>
                </TableCell>
                {forecastData.map((data, index) => (
                  <TableCell
                    key={data.month}
                    className={`text-center font-semibold text-green-700 ${data.isPast ? "bg-blue-50" : ""}`}
                  >
                    {formatDelta(getNewMentorsNeeded(data, index))}
                  </TableCell>
                ))}
              </TableRow>

              {/* Expanded Male/Female Rows */}
              {expandedRows.mentors && (
                <>
                  <TableRow className="bg-green-50/50">
                    <TableCell className="pl-6 text-sm text-gray-600">↳ Male</TableCell>
                    {forecastData.map((data, index) => {
                      const newIdeal = getNewMentorsNeeded(data, index);
                      const newMale = Math.round(newIdeal * (settings.malePercent / 100));
                      return (
                        <TableCell key={data.month} className={`text-center text-sm text-gray-600 ${data.isPast ? "bg-blue-50" : ""}`}>
                          {formatDelta(newMale)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  <TableRow className="bg-green-50/50">
                    <TableCell className="pl-6 text-sm text-gray-600">↳ Female</TableCell>
                    {forecastData.map((data, index) => {
                      const newIdeal = getNewMentorsNeeded(data, index);
                      const newMale = Math.round(newIdeal * (settings.malePercent / 100));
                      const newFemale = newIdeal - newMale;
                      return (
                        <TableCell key={data.month} className={`text-center text-sm text-gray-600 ${data.isPast ? "bg-blue-50" : ""}`}>
                          {formatDelta(newFemale)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </>
              )}

              {/* Mentors Leaving Row */}
              <TableRow className="bg-red-50/50">
                <TableCell className="font-medium text-red-700">Mentors Leaving</TableCell>
                {forecastData.map((data) => {
                  const actualLeaving = actualData[data.monthIndex]?.deactivatedMentors;
                  const leavingValue = data.isPast && actualLeaving !== undefined
                    ? actualLeaving
                    : (mentorsLeaving[data.monthIndex] ?? 0);
                  return (
                    <TableCell key={data.month} className={`text-center ${data.isPast ? "bg-blue-50" : ""}`}>
                      {data.isPast ? (
                        <span className="font-semibold text-red-600">{leavingValue}</span>
                      ) : (
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          defaultValue={leavingValue}
                          key={`leaving-${selectedYear}-${data.monthIndex}-${leavingValue}`}
                          onBlur={(e) => onMentorsLeavingChange(data.monthIndex, e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                          className="w-12 appearance-none border-b-2 border-dashed border-red-400 bg-transparent py-0.5 text-center font-semibold text-red-600 outline-none transition-all focus:border-red-600 focus:border-solid"
                        />
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>

              {/* Lead Mentors Row */}
              <TableRow>
                <TableCell className="font-medium">Lead Mentors</TableCell>
                {forecastData.map((data) => (
                  <TableCell key={data.month} className={`text-center ${data.isPast ? "bg-blue-50" : ""}`}>
                    {data.isPast && data.actualLeads !== undefined ? data.actualLeads : data.idealLeads}
                  </TableCell>
                ))}
              </TableRow>

              {/* New Lead Mentors Needed Row */}
              <TableRow className="bg-purple-50/50">
                <TableCell className="font-medium text-purple-700">New Lead Mentors Needed</TableCell>
                {forecastData.map((data, index) => (
                  <TableCell
                    key={data.month}
                    className={`text-center font-semibold text-purple-700 ${data.isPast ? "bg-blue-50" : ""}`}
                  >
                    {formatDelta(getNewLeadsNeeded(data, index))}
                  </TableCell>
                ))}
              </TableRow>

              {/* Supervisors Row */}
              <TableRow>
                <TableCell className="font-medium">Supervisors</TableCell>
                {forecastData.map((data) => (
                  <TableCell key={data.month} className={`text-center ${data.isPast ? "bg-blue-50" : ""}`}>
                    {data.isPast && data.actualSupervisors !== undefined ? data.actualSupervisors : data.idealSupervisors}
                  </TableCell>
                ))}
              </TableRow>

              {/* New Supervisors Needed Row */}
              <TableRow className="bg-orange-50">
                <TableCell className="font-medium text-orange-700">New Supervisors Needed</TableCell>
                {forecastData.map((data, index) => (
                  <TableCell
                    key={data.month}
                    className={`text-center font-semibold text-orange-700 ${data.isPast ? "bg-blue-50" : ""}`}
                  >
                    {formatDelta(getNewSupervisorsNeeded(data, index))}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
